import {
  BUILTIN_PLUGINS,
  PLUGINS,
  defaultOnAddons,
  explainResolution,
  type PluginProblem,
  type PluginRegistry,
} from '@dunea/shared';
import type { AppPluginStatus } from '~/lib/api/plugins';

// View model for the Plugins section of the Integrations panel: the app's
// plugins (app_plugins rows plus host add-ons that are on by default), and the
// registry's resolution problems in plain language.

export interface PluginRowView {
  id: string;
  title: string;
  description: string;
  icon: string;
  state: 'enabled' | 'disabled';
  /** Plain-language "who turned it on". */
  sourceLabel: string;
  /** Built-ins can't be turned off. */
  canToggle: boolean;
  /** Problems the registry reports for this plugin (missing requirements, conflicts). */
  problems: PluginProblem[];
}

const SOURCE_LABELS: Record<string, string> = {
  agent: 'Added by Dunea',
  owner: 'Set by you',
  detected: 'Found in your code',
  default: 'On by default',
};

export function sourceLabel(source: string): string {
  return SOURCE_LABELS[source] ?? source;
}

export function pluginRows(status: AppPluginStatus, registry: PluginRegistry = PLUGINS): PluginRowView[] {
  const problems = explainResolution(status.resolution, registry);
  const known = status.plugins.filter((p) => registry.get(p.id));
  const enabled = known.filter((p) => p.state === 'enabled').map((p) => p.id);
  const listed = new Set(known.map((p) => p.id));
  const addons = defaultOnAddons(enabled, registry)
    .filter((id) => !listed.has(id))
    .map((id) => ({ id, state: 'enabled' as const, source: 'default' }));

  return [...known, ...addons].map((p) => {
    const manifest = registry.get(p.id)!;

    return {
      id: p.id,
      title: manifest.title,
      description: manifest.description,
      icon: manifest.icon ?? 'i-ph:puzzle-piece',
      state: p.state,
      sourceLabel: sourceLabel(p.source),
      canToggle: !BUILTIN_PLUGINS.some((b) => b.id === p.id),
      problems: p.state === 'enabled' ? problems.filter((x) => x.plugin === p.id) : [],
    };
  });
}

/** Problems not tied to a listed plugin (e.g. an unknown id), shown above the list. */
export function generalProblems(status: AppPluginStatus, rows: readonly PluginRowView[], registry = PLUGINS) {
  const ids = new Set(rows.map((r) => r.id));
  return explainResolution(status.resolution, registry).filter((p) => !p.plugin || !ids.has(p.plugin));
}
